/**
 * Territory overlay.
 *
 * One RGBA texture at 1 pixel per tile, upscaled x4 with nearest-neighbour
 * filtering, so ownership reads as chunky pixel-art blocks with opaque borders.
 * Only dirty tiles are repainted, and the upload is limited to their bounding
 * box.
 */

import { Sprite, Texture } from 'pixi.js';
import { ART, MAP_H, MAP_W } from '../../shared/constants';
import { state } from '../state';

const FILL_ALPHA = 118;
const BORDER_ALPHA = 245;

export class TerritoryLayer {
  readonly sprite: Sprite;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private img: ImageData;
  private texture: Texture;

  // player colour lookup, indexed by player id
  private cr = new Uint8Array(256);
  private cg = new Uint8Array(256);
  private cb = new Uint8Array(256);

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = MAP_W;
    this.canvas.height = MAP_H;
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;
    this.img = this.ctx.createImageData(MAP_W, MAP_H);
    this.texture = Texture.from(this.canvas);
    try { (this.texture.source as any).scaleMode = 'nearest'; } catch { /* ignore */ }
    this.sprite = new Sprite(this.texture);
    this.sprite.scale.set(ART);
  }

  refreshColors() {
    for (const p of state.players.values()) {
      if (p.id > 255) continue;
      this.cr[p.id] = (p.color >> 16) & 255;
      this.cg[p.id] = (p.color >> 8) & 255;
      this.cb[p.id] = p.color & 255;
    }
  }

  redrawAll() {
    state.dirty.clear();
    for (let i = 0; i < MAP_W * MAP_H; i++) this.paint(i);
    this.ctx.putImageData(this.img, 0, 0);
    this.texture.source.update();
  }

  update() {
    if (state.fullRedraw) {
      state.fullRedraw = false;
      this.refreshColors();
      this.redrawAll();
      return;
    }
    if (state.dirty.size === 0) return;

    let minX = MAP_W, minY = MAP_H, maxX = 0, maxY = 0;
    for (const tile of state.dirty) {
      this.paint(tile);
      const x = tile % MAP_W, y = (tile / MAP_W) | 0;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    state.dirty.clear();

    this.ctx.putImageData(this.img, 0, 0, minX, minY, maxX - minX + 1, maxY - minY + 1);
    this.texture.source.update();
  }

  private paint(tile: number) {
    const data = this.img.data;
    const o = tile * 4;
    const owner = state.owner[tile];
    if (owner === 0) {
      data[o + 3] = 0;
      return;
    }
    const x = tile % MAP_W, y = (tile / MAP_W) | 0;
    let border = false;
    if (x === 0 || state.owner[tile - 1] !== owner) border = true;
    else if (x === MAP_W - 1 || state.owner[tile + 1] !== owner) border = true;
    else if (y === 0 || state.owner[tile - MAP_W] !== owner) border = true;
    else if (y === MAP_H - 1 || state.owner[tile + MAP_W] !== owner) border = true;

    let r = this.cr[owner], g = this.cg[owner], b = this.cb[owner];
    if (border) {
      r = Math.min(255, r * 1.35) | 0;
      g = Math.min(255, g * 1.35) | 0;
      b = Math.min(255, b * 1.35) | 0;
    }
    data[o] = r;
    data[o + 1] = g;
    data[o + 2] = b;
    data[o + 3] = border ? BORDER_ALPHA : FILL_ALPHA;
  }
}
